package br.com.heyagenda.domain.billing

import br.com.heyagenda.domain.model.BillingCycle
import br.com.heyagenda.domain.model.SubscriptionInfo
import br.com.heyagenda.domain.model.SubscriptionTier
import br.com.heyagenda.domain.repository.AuthRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

interface BillingService {
    val subscriptionInfo: StateFlow<SubscriptionInfo>
    suspend fun purchasePlan(tier: SubscriptionTier, billingCycle: BillingCycle): Result<SubscriptionInfo>
    suspend fun cancelSubscription(): Result<SubscriptionInfo>
    suspend fun restorePurchases(): Result<SubscriptionInfo>
    suspend fun presentPaywall(): Result<SubscriptionInfo>
    suspend fun setSimulatedTier(tier: SubscriptionTier)
}

class DefaultBillingService(
    private val authRepository: AuthRepository,
    private val appointmentRepository: br.com.heyagenda.domain.repository.AppointmentRepository? = null
) : BillingService {

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _subscriptionInfo = MutableStateFlow(SubscriptionInfo())
    override val subscriptionInfo: StateFlow<SubscriptionInfo> = _subscriptionInfo.asStateFlow()

    init {
        scope.launch {
            authRepository.currentUser.collectLatest { user ->
                if (user != null) {
                    PlatformBillingManager.initialize(user.uid)
                    val tier = SubscriptionTier.fromId(user.subscriptionTier)
                    val cycle = when (user.subscriptionBillingCycle?.lowercase()) {
                        "annual" -> BillingCycle.ANNUAL
                        else -> BillingCycle.MONTHLY
                    }
                    _subscriptionInfo.update {
                        it.copy(
                            tier = tier,
                            billingCycle = cycle,
                            isFounder = user.isFounderPromo,
                            expiresAt = user.subscriptionExpiresAt,
                            monthlyAppointmentsUsed = user.monthlyAppointmentsCount
                        )
                    }
                }
            }
        }

        if (appointmentRepository != null) {
            scope.launch {
                try {
                    val today = br.com.heyagenda.domain.model.CalendarUtils.getTodayIso()
                    val yearMonth = today.take(7)
                    appointmentRepository.getAppointmentsInRange("$yearMonth-01", "$yearMonth-31").collectLatest { list ->
                        _subscriptionInfo.update { it.copy(monthlyAppointmentsUsed = list.size) }
                    }
                } catch (_: Throwable) {}
            }
        }
    }

    override suspend fun purchasePlan(tier: SubscriptionTier, billingCycle: BillingCycle): Result<SubscriptionInfo> {
        return try {
            if (tier == SubscriptionTier.PRO) {
                val billingResult = PlatformBillingManager.purchase(billingCycle)
                if (billingResult.isFailure) {
                    return Result.failure(billingResult.exceptionOrNull() ?: Exception("Erro ao processar compra"))
                }
            }
            val expiresAt = PlatformBillingManager.getExpirationDateMillis()
            authRepository.updateSubscriptionTier(tier.id, billingCycle.name.lowercase(), expiresAt)
            _subscriptionInfo.update {
                it.copy(
                    tier = tier,
                    billingCycle = billingCycle,
                    isActive = true,
                    expiresAt = expiresAt
                )
            }
            Result.success(_subscriptionInfo.value)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun cancelSubscription(): Result<SubscriptionInfo> {
        return try {
            authRepository.updateSubscriptionTier(SubscriptionTier.FREE.id, BillingCycle.ANNUAL.name.lowercase(), null)
            _subscriptionInfo.update {
                it.copy(
                    tier = SubscriptionTier.FREE,
                    billingCycle = BillingCycle.ANNUAL,
                    isActive = true,
                    expiresAt = null
                )
            }
            Result.success(_subscriptionInfo.value)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun restorePurchases(): Result<SubscriptionInfo> {
        return try {
            val restoreResult = PlatformBillingManager.restorePurchases()
            if (restoreResult.isSuccess && restoreResult.getOrDefault(false)) {
                val expiresAt = PlatformBillingManager.getExpirationDateMillis()
                authRepository.updateSubscriptionTier(SubscriptionTier.PRO.id, BillingCycle.ANNUAL.name.lowercase(), expiresAt)
                _subscriptionInfo.update {
                    it.copy(
                        tier = SubscriptionTier.PRO,
                        billingCycle = BillingCycle.ANNUAL,
                        isActive = true,
                        expiresAt = expiresAt
                    )
                }
            }
            Result.success(_subscriptionInfo.value)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun presentPaywall(): Result<SubscriptionInfo> {
        return try {
            val paywallResult = PlatformBillingManager.presentPaywall()
            if (paywallResult.isFailure) {
                return Result.failure(paywallResult.exceptionOrNull() ?: Exception("Paywall cancelada"))
            }
            val purchased = paywallResult.getOrDefault(false)
            if (!purchased) {
                return Result.success(_subscriptionInfo.value)
            }
            val expiresAt = PlatformBillingManager.getExpirationDateMillis()
            authRepository.updateSubscriptionTier(SubscriptionTier.PRO.id, BillingCycle.ANNUAL.name.lowercase(), expiresAt)
            _subscriptionInfo.update {
                it.copy(
                    tier = SubscriptionTier.PRO,
                    billingCycle = BillingCycle.ANNUAL,
                    isActive = true,
                    expiresAt = expiresAt
                )
            }
            Result.success(_subscriptionInfo.value)
        } catch (e: Throwable) {
            Result.failure(e)
        }
    }

    override suspend fun setSimulatedTier(tier: SubscriptionTier) {
        try {
            authRepository.updateSubscriptionTier(tier.id, BillingCycle.ANNUAL.name.lowercase())
        } catch (_: Throwable) {}
        _subscriptionInfo.update { it.copy(tier = tier, isActive = true) }
    }
}
